import { useAppConfig } from "../AppConfigProvider";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import withAuth from "../hocs/withAuth";
import noAuth from "../hocs/noAuth";
import { useRef } from "react";
import { routingInstrumentation } from "../utils/sentry";

export type RootStackParamList = {
  "My Day": undefined;
  Home: undefined;
  Productivity: { tab?: string };
  Portfolio: undefined;
  Analytics: undefined;
  "Student Analytics": undefined;
  Tools: undefined;
  Insights: undefined;
  Journal: undefined;
  Coursework: undefined;
  "Academic Plan": undefined;
  Activities: undefined;
  School: { integration_id: string };
  "Course Catalogue": { integration_id: string };
  BoxSchool: { integration_id: string };
  Course: undefined;
  "Custom Course Student": {
    resource_type?: string;
    resource_id?: string;
    course_id?: string;
    integration_id?: string;
  };
  Career: undefined;
  Degree: undefined;
  Content: undefined;
  ContentForm: undefined;
  Modules: undefined;
  Journey: { id: string };
  Project: { id: string };
  CareerDetails: undefined;
  Resource: {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
  };
  "Custom Resource Student": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
  };
  "Custom Assignment Student": {
    assignment_id: string;
    course_id: string;
    integration_id: string;
  };
  "Custom Course AITutor": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
  };
  "Custom Course Assessment": {
    assessment_id: string;
    course_id: string;
    integration_id: string;
  };
  Learn: undefined;
  Categories: undefined;
  Tracks: {
    field: string;
    subfield: string;
  };
  "Track Overview": undefined;
  Lesson: {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
  };
  "Lesson Activities": {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
    loTitle: string;
    loId: string;
  };
  "Lesson Assessment": {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
    loTitle: string;
    loId: string;
  };
  "Lesson Chat": { loId: string };
  "Lesson Video": undefined;
  Health: undefined;
  Onboarding: undefined;
  Settings: undefined;
  Faculty: {
    courseName: string;
    courseId: string;
    integration_id: string;
  };
  Invite: undefined;
  Login: { isLMS: boolean };
  LoginLTI: { token?: string };
  Signup: { isLMS: boolean };
  "Reset Password": { token?: string };
  "Set Password": { token?: string; f?: string; uid?: string; service?: string };
  "Verify Email": undefined;
  ThankYou: undefined;
  ThankYouCoursePayment: undefined;
  EdlinkSuccess: undefined;
  "Custom Coursework": undefined;
  "Custom Coursework Course Form": {
    courseId: string;
  };
  "Custom School Student": { integration_id: string };
  "Custom Coursework Modules": {
    courseId: string;
    aiTutorIndex?: number;
    pageIndex?: number;
    moduleIndex?: number;
  };
  Admin: undefined;
  "Tenant Dashboard": undefined;
  "Tenant Settings": { id: string };
  Assessment: { assessmentId: string; courseId: string; integrationId: string };
  "Grading Center": { assessmentId: string; courseId: string; integrationId: string };
  "Course Settings": { courseId: string; tab?: string };
};

const Stack = createStackNavigator<RootStackParamList>();

function AppNavigator() {
  const appConfig = useAppConfig();
  const navigation = useRef();

  const currentDomain = appConfig.currentDomain;
  const isLMS = appConfig.isLMS;
  const isAxioOffering = appConfig.isAxioOffering;

  const linking = {
    prefixes: [`https://${currentDomain}`, "primer://"],
    config: {
      screens: {
        "My Day": "my-day",
        Home: "home",
        Productivity: "productivity",
        Porfolio: "portfolio",
        Analytics: "analytics",
        "Student Analytics": "student-analytics",
        "Academic Plan": "academic-plan",
        Journal: "journal",
        Insights: "student/insights",
        Coursework: "coursework",
        Activities: "activities",
        School: "school",
        "Course Catalogue": "school/course-catalogue",
        "Custom School Student": "student/school",
        Course: "school/course",
        "Custom Course Student": "student/school/course",
        Resource: "school/course/resource",
        "Custom Resource Student": "student/school/course/resource",
        "Custom Assignment Student": "student/school/course/assignment",
        "Custom Course AITutor": "student/school/course/aitutor",
        "Custom Course Assessment": "student/school/course/assessment",
        Learn: "learn",
        Tracks: "tracks",
        "Track Overview": "track/overview",
        Categories: "categories",
        Lesson: "learn/lesson",
        "Lesson Activities": "learn/lesson/activities",
        "Lesson Assessment": "learn/lesson/assessment",
        "Lesson Chat": "learn/lesson/chat",
        "Lesson Video": "learn/lesson/video",
        Health: "health",
        Onboarding: "onboarding",
        Settings: "settings",
        Faculty: "faculty",
        Invite: "invite",
        Login: "login",
        LoginLTI: "login/lti",
        Signup: "signup",
        "Reset Password": "reset-password",
        "Set Password": "set-password",
        "Verify Email": "verify-email",
        Career: "career",
        Degree: "degree",
        Content: "content",
        Modules: "content/modules",
        ContentForm: "content/form",
        Journey: "journey",
        Project: "project",
        CareerDetails: "career/details",
        ThankYou: "thank-you",
        ThankYouCoursePayment: "thank-you-course-payment",
        EdlinkSuccess: "edlink-success",
        "Custom Coursework": "custom-coursework",
        "Custom Coursework Course Form": "custom-coursework/course-form",
        "Custom Coursework Modules": "custom-coursework/modules",
        Admin: "dashboard",
        "Tenant Dashboard": "dashboard/tenent",
        "Tenant Settings": "dashboard/tenent/settings",
        Assessment: "assessment",
        "Grading Center": "school/grading-center",
        "Course Settings": "school/course/settings",
      },
    },
  };

  const DashboardScreen = withAuth(require("../views/DashboardView").default);
  const HomeScreen = withAuth(require("../views/HomeView").default);
  const ProductivityScreen = withAuth(require("../views/ProductivityView").default);
  const PortfolioScreen = withAuth(require("../views/PortfolioView").default);
  const AnalyticsScreen = withAuth(require("../views/AnalyticsView").default);
  const InsightsScreen = withAuth(require("../views/InsightsView").default);
  const StudentAnalyticsScreen = withAuth(require("../views/StudentAnalyticsView").default);
  const JournalScreen = withAuth(require("../views/JournalView").default);
  const SchoolsScreen = withAuth(require("../views/SchoolsView").default);
  const AcademicPlanScreen = withAuth(require("../views/AcademicPlanView").default);
  const SchoolScreen = withAuth(require("../views/SchoolView").default);
  const CourseCatalogueScreen = withAuth(require("../views/CourseCatalogueView").default);
  const CustomSchoolStudentScreen = withAuth(require("../views/CustomSchoolStudentView").default);
  const CourseScreen = withAuth(require("../views/CourseView").default);
  const CustomCourseStudentScreen = withAuth(require("../views/CustomCourseStudentView").default);
  const CareerScreen = withAuth(require("../views/CareerView").default);
  const DegreeScreen = withAuth(require("../views/DegreeView").default);
  const JourneyScreen = withAuth(require("../views/JourneyView").default);
  const ProjectScreen = withAuth(require("../views/ProjectView").default);
  const CareerDetailScreen = withAuth(require("../views/CareerDetailView").default);
  const ResourceScreen = withAuth(require("../views/ResourceView").default);
  const CustomResourceStudentScreen = withAuth(require("../views/CustomResourceStudentView").default);
  const CustomAssignmentStudentScreen = withAuth(require("../views/CustomAssignmentStudentView").default);
  const CustomCourseAITutorChatScreen = withAuth(require("../views/CustomCourseAITutorChatView").default);
  const CustomCourseAssessmentStudentScreen = withAuth(require("../views/CustomCourseAssessmentStudentView").default);
  const LearnScreen = withAuth(require("../views/LearnView").default);
  const CategoriesScreen = withAuth(require("../views/CategoriesView").default);
  const TracksScreen = withAuth(require("../views/TracksView").default);
  const TrackOverviewScreen = withAuth(require("../views/TrackOverviewView").default);
  const LessonSubchapterScreen = withAuth(require("../views/LessonSubchapterView").default);
  const LessonLearningScreen = withAuth(require("../views/LessonLearningView").default);
  const LessonAssessmentScreen = withAuth(require("../views/LessonAssessmentView").default);
  const LessonChatScreen = withAuth(require("../views/LessonChatView").default);
  const LessonVideoScreen = withAuth(require("../views/LessonVideoView").default);
  const HealthScreen = withAuth(require("../views/HealthView").default);
  const OnboardingScreen = withAuth(require("../views/OnboardingView").default);
  const SettingsScreen = withAuth(require("../views/SettingsView").default);
  const InviteScreen = withAuth(require("../views/InviteView").default);
  const ThankYouScreen = withAuth(require("../views/ThankYouView").default);
  const EdlinkSuccessScreen = withAuth(require("../views/EdlinkSuccessView").default);
  const CustomCourseworkScreen = withAuth(require("../views/CustomCourseworkView").default);
  const FacultyScreen = withAuth(require("../views/FacultyView").default);
  const CustomCourseworkModulesScreen = withAuth(require("../views/CustomCourseworkModulesView").default);
  const CustomCourseworkCourseFormScreen = withAuth(require("../views/CustomCourseworkCourseFormView").default);
  const AdminScreen = withAuth(require("../views/AdminView").default);
  const TenantDashboardScreen = withAuth(require("../views/TenantDashboardView").default);
  const TenantSettingsDashboardScreen = withAuth(require("../views/TenantSettingsDashboardView").default);
  const LoginScreen = noAuth(require("../views/LoginView").default);
  const LoginLTIScreen = noAuth(require("../views/LoginLTIView").default);
  const SignupScreen = noAuth(require("../views/SignupView").default);
  const ResetPasswordScreen = noAuth(require("../views/ResetPasswordView").default);
  const SetPasswordScreen = require("../views/SetPasswordView").default;
  const VerifyEmailScreen = noAuth(require("../views/VerifyEmailView").default);
  const GradingCenterScreen = withAuth(require("../views/GradingCenterView").default);
  const CourseSettingsScreen = withAuth(require("../views/CourseSettingsView").default);
  return (
    <NavigationContainer
      linking={linking}
      ref={navigation}
      onReady={() => {
        routingInstrumentation.registerNavigationContainer(navigation);
      }}>
      <Stack.Navigator initialRouteName={isAxioOffering ? "Home" : "My Day"} screenOptions={{ headerShown: false }}>
        <Stack.Screen name="My Day" getComponent={() => DashboardScreen} />
        <Stack.Screen name="Home" getComponent={() => HomeScreen} />
        <Stack.Screen name="Productivity" getComponent={() => ProductivityScreen} />
        <Stack.Screen name="Portfolio" getComponent={() => PortfolioScreen} />
        <Stack.Screen name="Analytics" getComponent={() => AnalyticsScreen} />
        <Stack.Screen name="Insights" getComponent={() => InsightsScreen} />
        <Stack.Screen name="Student Analytics" getComponent={() => StudentAnalyticsScreen} />
        <Stack.Screen name="Journal" getComponent={() => JournalScreen} />
        <Stack.Screen name="Coursework" getComponent={() => SchoolsScreen} />
        <Stack.Screen name="Academic Plan" getComponent={() => AcademicPlanScreen} />
        <Stack.Screen name="Activities" getComponent={() => SchoolsScreen} />
        <Stack.Screen name="School" getComponent={() => SchoolScreen} />
        <Stack.Screen name="Course Catalogue" getComponent={() => CourseCatalogueScreen} />
        <Stack.Screen name="Custom School Student" getComponent={() => CustomSchoolStudentScreen} />
        <Stack.Screen name="Course" getComponent={() => CourseScreen} />
        <Stack.Screen name="Custom Course Student" getComponent={() => CustomCourseStudentScreen} />
        <Stack.Screen name="Career" getComponent={() => CareerScreen} />
        <Stack.Screen name="Degree" getComponent={() => DegreeScreen} />
        <Stack.Screen name="Journey" getComponent={() => JourneyScreen} />
        <Stack.Screen name="Project" getComponent={() => ProjectScreen} />
        <Stack.Screen name="CareerDetails" getComponent={() => CareerDetailScreen} />
        <Stack.Screen name="Resource" getComponent={() => ResourceScreen} />
        <Stack.Screen name="Custom Resource Student" getComponent={() => CustomResourceStudentScreen} />
        <Stack.Screen name="Custom Assignment Student" getComponent={() => CustomAssignmentStudentScreen} />
        <Stack.Screen name="Custom Course AITutor" getComponent={() => CustomCourseAITutorChatScreen} />
        <Stack.Screen name="Custom Course Assessment" getComponent={() => CustomCourseAssessmentStudentScreen} />
        <Stack.Screen name="Learn" getComponent={() => LearnScreen} />
        <Stack.Screen name="Categories" getComponent={() => CategoriesScreen} />
        <Stack.Screen name="Tracks" getComponent={() => TracksScreen} />
        <Stack.Screen name="Track Overview" getComponent={() => TrackOverviewScreen} />
        <Stack.Screen name="Lesson" getComponent={() => LessonSubchapterScreen} />
        <Stack.Screen name="Lesson Activities" getComponent={() => LessonLearningScreen} />
        <Stack.Screen name="Lesson Assessment" getComponent={() => LessonAssessmentScreen} />
        <Stack.Screen name="Lesson Chat" getComponent={() => LessonChatScreen} />
        <Stack.Screen name="Lesson Video" getComponent={() => LessonVideoScreen} />
        <Stack.Screen name="Health" getComponent={() => HealthScreen} />
        <Stack.Screen name="Onboarding" getComponent={() => OnboardingScreen} />
        <Stack.Screen name="Settings" getComponent={() => SettingsScreen} />
        <Stack.Screen name="Faculty" getComponent={() => FacultyScreen} />
        <Stack.Screen name="Invite" getComponent={() => InviteScreen} />
        <Stack.Screen name="Login" getComponent={() => LoginScreen} initialParams={{ isLMS }} />
        <Stack.Screen name="LoginLTI" getComponent={() => LoginLTIScreen} />
        <Stack.Screen name="Signup" getComponent={() => SignupScreen} initialParams={{ isLMS }} />
        <Stack.Screen name="Reset Password" getComponent={() => ResetPasswordScreen} />
        <Stack.Screen name="Set Password" getComponent={() => SetPasswordScreen} />
        <Stack.Screen name="Verify Email" getComponent={() => VerifyEmailScreen} />
        <Stack.Screen name="ThankYou" getComponent={() => ThankYouScreen} />
        <Stack.Screen name="EdlinkSuccess" getComponent={() => EdlinkSuccessScreen} />
        <Stack.Screen name="Custom Coursework" getComponent={() => CustomCourseworkScreen} />
        <Stack.Screen name="Custom Coursework Course Form" getComponent={() => CustomCourseworkCourseFormScreen} />
        <Stack.Screen name="Custom Coursework Modules" getComponent={() => CustomCourseworkModulesScreen} />
        <Stack.Screen name="Admin" getComponent={() => AdminScreen} />
        <Stack.Screen name="Tenant Dashboard" getComponent={() => TenantDashboardScreen} />
        <Stack.Screen name="Tenant Settings" getComponent={() => TenantSettingsDashboardScreen} />
        <Stack.Screen name="Grading Center" getComponent={() => GradingCenterScreen} />
        <Stack.Screen name="Course Settings" getComponent={() => CourseSettingsScreen} />
      </Stack.Navigator>
    </NavigationContainer>
  );
}

export default AppNavigator;
